import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertType, AlertPriority, EntityType, getPriorityColor, getRelativeTime, alertsApi } from '../services/alertsService';
import { Check, X, Users, FileText, Wallet, Bell } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { handleApiError } from '../utils/errorHandler';

const getAlertTypeIcon = (type: AlertType) => {
  switch (type) {
    case AlertType.ClientAssigned: return <Users size={18} />;
    case AlertType.ClientDocumentUploaded: return <FileText size={18} />;
    case AlertType.OutstandingDebtOverdue: return <Wallet size={18} />;
    default: return <Bell size={18} />;
  }
};

interface AlertItemProps {
  alert: Alert;
  onRead: (alertId: number) => void;
  onHandled: (alertId: number) => void;
  onDelete: (alertId: number) => void;
}

const Container = styled.div<{ $priority: AlertPriority }>`
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  border-right: 3px solid ${props => getPriorityColor(props.$priority)};
  background: var(--surface-color);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const IconAndContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const Icon = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--text-secondary);
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Message = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.4;
  margin-bottom: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
`;

const TimeAndEntity = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-tertiary);
`;

const Time = styled.span``;

const EntityBadge = styled.span`
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
  background: var(--background-color);
  font-size: 11px;
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  gap: 4px;
`;

const ActionButton = styled.button<{ $variant?: 'danger' | 'success' | 'default' }>`
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 500;
  border: none;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${props => {
    if (props.$variant === 'danger') return 'var(--error-light)';
    if (props.$variant === 'success') return 'var(--success-light)';
    return 'var(--info-light)';
  }};
  color: ${props => {
    if (props.$variant === 'danger') return 'var(--error-color)';
    if (props.$variant === 'success') return 'var(--success-color)';
    return 'var(--info-color)';
  }};

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const PriorityBadge = styled.div<{ $priority: AlertPriority }>`
  position: absolute;
  top: 12px;
  left: 16px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => getPriorityColor(props.$priority)};
`;

const AlertItem: React.FC<AlertItemProps> = ({ alert, onRead, onHandled, onDelete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    // Don't trigger if clicking on action buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    try {
      setLoading(true);

      // Mark as read if unread
      if (alert.status === 0) {
        await alertsApi.markAsRead(alert.id);
        onRead(alert.id);
      }

      // Navigate to entity
      if (alert.entityType !== undefined && alert.entityId) {
        if (alert.entityType === EntityType.Lead) {
          navigate(`/leads`);
        } else if (alert.entityType === EntityType.Client) {
          navigate(`/clients/${alert.entityId}/manage`);
        }
      }
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטיפול בהתראה'));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsHandled = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setLoading(true);
      await alertsApi.markAsHandled(alert.id, {});
      onHandled(alert.id);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בסימון ההתראה כטופלה'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('האם אתה בטוח שברצונך למחוק התראה זו?')) {
      return;
    }

    try {
      setLoading(true);
      await alertsApi.deleteAlert(alert.id);
      onDelete(alert.id);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה במחיקת ההתראה'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container $priority={alert.priority as AlertPriority} onClick={handleClick}>
      <PriorityBadge $priority={alert.priority as AlertPriority} />
      <Header>
        <IconAndContent>
          <Icon>{getAlertTypeIcon(alert.alertType as AlertType)}</Icon>
          <Content>
            <Title>{alert.title}</Title>
            <Message>{alert.message}</Message>
          </Content>
        </IconAndContent>
      </Header>

      <Footer>
        <TimeAndEntity>
          <Time>{getRelativeTime(alert.createdAt)}</Time>
          {alert.entityName && (
            <EntityBadge>{alert.entityName}</EntityBadge>
          )}
        </TimeAndEntity>

        <Actions>
          {alert.status === 0 && (
            <ActionButton
              $variant="default"
              onClick={handleMarkAsHandled}
              disabled={loading}
              title="סמן כטופל"
            >
              <Check size={14} />
              טופל
            </ActionButton>
          )}
          <ActionButton
            $variant="danger"
            onClick={handleDelete}
            disabled={loading}
            title="מחק"
          >
            <X size={14} />
          </ActionButton>
        </Actions>
      </Footer>
    </Container>
  );
};

export default AlertItem;
